
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { useAdminActions } from '@/hooks/useAdminActions';
import { useAdminFilters } from '@/hooks/useAdminFilters';
import { useAdminData } from '@/hooks/useAdminData';
import { usePagination } from '@/hooks/usePagination';
import { IdealProfile } from '@/types/dashboardTypes';

export const useDashboardActions = (
  adminData: ReturnType<typeof useAdminData>,
  adminFilters: ReturnType<typeof useAdminFilters>,
  behavioralPagination: ReturnType<typeof usePagination>
) => {
  const { toast } = useToast();
  const { signOut } = useAuth();
  const { openCustomerPortal } = useSubscription();
  const navigate = useNavigate();
  const adminActions = useAdminActions();

  const handleViewDetails = (submission: any) => {
    adminActions.handleViewDetails(submission);
  };

  // This function is called AFTER deletion succeeds - optimistically remove from UI
  const handleSubmissionDeleted = async (submissionId: string) => {
    // Optimistically remove the submission from UI immediately
    adminData.removeSubmissionOptimistically(submissionId);
    
    // Handle pagination if this was the last item on the page
    const currentPageItems = adminData.behavioralSubmissions.length - 1; // -1 because we just removed one
    const isLastItemOnPage = currentPageItems === 0;
    const isNotFirstPage = behavioralPagination.currentPage > 1;
    
    if (isLastItemOnPage && isNotFirstPage) {
      behavioralPagination.setCurrentPage(behavioralPagination.currentPage - 1);
    }
  };

  const handleDeleteProfile = async (profile: IdealProfile) => {
    await adminActions.handleDeleteIdealProfile(
      profile,
      adminData.removeIdealProfileOptimistically,
      adminData.restoreIdealProfile,
      adminData.setProfileDeleting
    );
  };

  const handleFiltersChange = (filters: any) => {
    adminFilters.handleBehavioralFiltersChange(filters);
    behavioralPagination.resetToFirstPage();
  };

  const handleSearchChange = (term: string) => {
    adminFilters.handleSearchChange(term);
    behavioralPagination.resetToFirstPage();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open customer portal. Please try again.",
        variant: "destructive"
      });
    }
  };

  return {
    handleViewDetails,
    handleSubmissionDeleted,
    handleDeleteProfile,
    handleFiltersChange,
    handleSearchChange,
    handleSignOut,
    handleManageSubscription,
    selectedSubmission: adminActions.selectedSubmission,
    isModalOpen: adminActions.isModalOpen,
    handleCloseModal: adminActions.handleCloseModal
  };
};
